import React from "react";
import { Link } from "react-router-dom";
import { Film, MapPin } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import api from "../../lib/apiClient";

/**
 * FeedNearbyVideosPanel — scrolling list of the 10 videos whose
 * filming location is closest to the currently-playing video.
 *
 * Iter-57++++++ (May 29, 2026): replaces the prior `FeedNearbyMap`
 * mini-map. Sort: ascending distance (closest = first row). Hooks
 * into the existing player by re-navigating to `/<username>?video=<id>`
 * so the user can hop laterally without leaving the player surface.
 *
 * Sub-block of the unified right-column card — no outer rounded
 * chrome; the parent's `divide-y` owns the frame.
 */
const FeedNearbyVideosPanel = ({ activeVideo, handleClosePlayer }) => {
  const videoId = activeVideo?.id;
  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!videoId) { setVideos([]); return; }
    const fetchNearby = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/videos/${videoId}/nearby-videos?radius_km=2000&limit=10`);
        if (!cancelled) setVideos(r.data?.videos || []);
      } catch (_err) {
        if (!cancelled) setVideos([]);
      }
      if (!cancelled) setLoading(false);
    };
    fetchNearby();
    return () => { cancelled = true; };
  }, [videoId]);

  return (
    <div className="flex p-2.5 flex-1 flex-col min-h-0" data-testid="feed-nearby-videos">
      <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
        <Film className="h-3 w-3 text-emerald-400" /> Nearby Videos
      </p>
      <ScrollArea className="flex-1 -mx-1 px-1 max-h-[180px] lg:max-h-none">
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-gray-600 text-[11px] italic py-3 text-center">No videos nearby yet.</p>
        ) : (
          <div className="space-y-1.5">
            {videos.map((v) => (
              <Link
                key={v.id}
                to={`/${v.username}?video=${v.id}`}
                onClick={handleClosePlayer}
                className="block p-2 rounded bg-gray-800/40 hover:bg-gray-700/50 transition border border-gray-800/50 hover:border-emerald-500/30"
                data-testid={`nearby-video-${v.id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-gray-200 font-medium text-[12px] truncate">{v.title}</span>
                  <span className="text-gray-500 tabular-nums text-[10px] flex-shrink-0">
                    {v.distance_km < 10 ? `${v.distance_km.toFixed(1)} km` : `${Math.round(v.distance_km)} km`}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                  <span className="text-emerald-400 truncate">@{v.username}</span>
                  {(v.location?.city || v.location?.country) && (
                    <span className="flex items-center gap-0.5 truncate">
                      <MapPin className="h-2.5 w-2.5 text-gray-600 flex-shrink-0" />
                      <span className="truncate">
                        {v.location.city}{v.location.city && v.location.country ? ", " : ""}{v.location.country}
                      </span>
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default FeedNearbyVideosPanel;
